import {
  Component,
  Host,
  h,
  Element,
  Listen,
  State,
  Method,
  Prop
} from "@stencil/core";

import { prefersReducedMotion } from "../../utils/animation-utils";
import { StzhFocusEvent } from "../../index";

const HELPER_FADE_TIME = 400;
const MONITOR_INTERVAL = 250;

const CLASS_FOCUS = "has-focus";
const CLASS_TARGET_FOCUSED = "stzh-fylingfocus-focused";
const CLASS_CONTAINER_ENABLED = "stzh-flyingfocus-enabled";

/*
 * Flyingfocus component.
 * Inpsired by: https://github.com/Q42/floating-focus-a11y
 * https://engineering.q42.nl/floating-focus/
 */
@Component({
  tag: "stzh-flyingfocus",
  styleUrl: "stzh-flyingfocus.scss",
  scoped: true
})
export class StzhFlyingfocus {
  @Element() element: HTMLStzhFlyingfocusElement;

  @State() style: {
    color: string
    borderStyle: string
    borderWidth: string
    borderBottomLeftRadius: string
    borderBottomRightRadius: string
    borderTopLeftRadius: string
    borderTopRightRadius: string
  };

  @State() position: {
    left: string
    top: string
    width: string
    height: string
  };

  @Prop() minimalEdgeSpace: number = 2;

  @State() enabled: boolean = false;
  @State() visible: boolean = false;
  @State() helper: boolean = false;
  @State() moving: boolean = false;

  @Listen("keydown", { target: "document" })
  handleKeydown(e: KeyboardEvent) {
    // show animation only upon Tab or Arrow keys press.
    if (e.keyCode !== 9 && !(e.keyCode > 36 && e.keyCode < 41)) {
      return;
    }

    this.enableFloatingFocus();
  }

  @Listen("mousedown", { target: "document" })
  handleMousedown() {
    this.disableFloatingFocus();
  }

  @Listen("focus", { target: "document", capture: true })
  @Listen("stzhFocus", { target: "document", capture: true })
  handleFocus(e: FocusEvent | CustomEvent<StzhFocusEvent>) {
    const detail = e.detail as StzhFocusEvent;
    let target = (detail?.originalEvent?.target || e.target) as Element;

    if (e.type === "focus") {
      // pierce through shadow DOM
      target = document.activeElement;

      while (target.shadowRoot && target.shadowRoot.activeElement) {
        target = target.shadowRoot.activeElement;
      }
    }

    if (!this.container) {
      return;
    }

    if (target === this.element || !this.container.contains(document.activeElement)) {
      this.handleBlur();
      return;
    }

    this.visible = true;
    this.helper = true;

    if (!prefersReducedMotion()) {
      this.moving = true;
    }

    const focusTargetAttribute = target.getAttribute('focus-target');
    if (focusTargetAttribute) {
      const root = ((target.getRootNode() as HTMLElement) || document);
      target = root.querySelector(`#${focusTargetAttribute}`) || target;
    }

    this.target = target;

    // make sure we can read the target style (even when refocussing the viewport)
    this.target.classList.remove(CLASS_TARGET_FOCUSED);
    this.target.classList.add(CLASS_FOCUS);

    this.resolveTargetOutlineStyle();
    this.repositionElement();

    this.target.classList.add(CLASS_TARGET_FOCUSED);

    clearTimeout(this.helperFadeTimeout);
    this.helperFadeTimeout = setTimeout(() => this.helper = false, HELPER_FADE_TIME);
  }

  @Listen("blur", { target: "document", capture: true })
  handleBlur() {
    this.visible = false;
    this.helper = false;

    if (!prefersReducedMotion()) {
      this.moving = false;
    }

    if (this.target) {
      this.target.classList.remove(CLASS_TARGET_FOCUSED);
      this.target.classList.remove(CLASS_FOCUS);
    }
  }

  @Listen("scroll", { target: "document", capture: true })
  @Listen("resize", { target: "window", capture: true })
  handleScrollResize() {
    if (this.debounceScroll) {
      window.cancelAnimationFrame(this.debounceScroll);
    }

    this.debounceScroll = requestAnimationFrame(this.repositionElement);
  }

  @Method()
  async reposition() {
    this.repositionElement();
  }

  private target: Element;
  private container: Element = document.documentElement;
  private previousTargetRect: DOMRect = null;
  private monitorElementPositionInterval: ReturnType<typeof setInterval>;
  private helperFadeTimeout: ReturnType<typeof setTimeout>;
  private debounceScroll: number;

  private repositionElement = () => {
    if (!this.target) {
      return;
    }

    this.position = this.getFloaterPosition();
  }

  private handleTransitionEnd = () => {
    this.moving = false;
  }

  private monitorElementPosition = () => {
    if (!this.target || !this.previousTargetRect || this.moving) {
      return;
    }

    const { left, top, width, height } = this.target.getBoundingClientRect();
    const { left: leftPrev, top: topPrev, width: widthPrev, height: heightPrev } = this.previousTargetRect;

    if (left === leftPrev && top === topPrev && width === widthPrev && height === heightPrev) {
      return;
    }

    if (!prefersReducedMotion()) {
      this.moving = true;
    }

    this.repositionElement();
  }

  /**
   * Get the border radius that the outline element should have:
   * element border radius + outline width + outline offset
   */
  private getOffsetBorderRadius(baseRadius, outlineOffset, outlineWidth) {
    baseRadius = parseFloat(baseRadius);
    outlineOffset = parseFloat(outlineOffset);
    outlineWidth = parseFloat(outlineWidth);

    if (baseRadius === 0) {
      return '0px';
    }

    outlineOffset = Math.max(outlineOffset, 0);
    return `${baseRadius + outlineWidth + outlineOffset}px`;
  }

  private resolveTargetOutlineStyle() {
    const targetStyle = window.getComputedStyle(this.target);
    const outlineOffset = targetStyle.outlineOffset || null;
    const outlineWidth = targetStyle.outlineWidth || null;

    this.style = {
      color: targetStyle.outlineColor,
      borderStyle: targetStyle.outlineStyle,
      borderWidth: outlineWidth,
      borderBottomLeftRadius: this.getOffsetBorderRadius(targetStyle.borderBottomLeftRadius, outlineOffset, outlineWidth),
      borderBottomRightRadius: this.getOffsetBorderRadius(targetStyle.borderBottomRightRadius, outlineOffset, outlineWidth),
      borderTopLeftRadius: this.getOffsetBorderRadius(targetStyle.borderTopLeftRadius, outlineOffset, outlineWidth),
      borderTopRightRadius: this.getOffsetBorderRadius(targetStyle.borderTopRightRadius, outlineOffset, outlineWidth)
    };
  }

  private getFloaterPosition() {
    const targetStyle = window.getComputedStyle(this.target);
    const padding = parseFloat(targetStyle.outlineOffset || '0px');
    const outlineWidth = parseFloat(this.style.borderWidth || '0px');

    const rect = this.target.getBoundingClientRect();
    this.previousTargetRect = rect;

    // make sure flying element with padding fits into viewport
    // with a minimal space to the edge of the viewport
    // otherwise decrease or remove padding completely
    const scrollbarRightWidth = window.innerWidth - document.documentElement.clientWidth;
    const scrollbarBottomWidth = window.innerHeight - document.documentElement.clientHeight;
    const spaceForPaddingLeft = rect.left - outlineWidth - this.minimalEdgeSpace;
    const spaceForPaddingRight = window.innerWidth - scrollbarRightWidth - (rect.left + rect.width) - outlineWidth - this.minimalEdgeSpace;
    const spaceForPaddingTop = rect.top - outlineWidth - this.minimalEdgeSpace;
    const spaceForPaddingBottom = window.innerHeight - scrollbarBottomWidth - (rect.top + rect.height) - outlineWidth - this.minimalEdgeSpace;
  
    const paddingLeft = spaceForPaddingLeft < padding ? spaceForPaddingLeft : padding;   
    const paddingRight = spaceForPaddingRight < padding ? spaceForPaddingRight : padding;
    const paddingTop = spaceForPaddingTop < padding ? spaceForPaddingTop : padding;
    const paddingBottom = spaceForPaddingBottom < padding ? spaceForPaddingBottom : padding;

    const width = rect.width + paddingLeft + paddingRight;
    const height = rect.height + paddingTop + paddingBottom;
    const left = window.scrollX + rect.left - paddingLeft + (width / 2);
    const top = window.scrollY + rect.top - paddingTop + (height / 2);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  }

  private enableFloatingFocus() {
    this.container.classList.add(CLASS_CONTAINER_ENABLED);
    this.enabled = true;
    clearInterval(this.monitorElementPositionInterval);
    this.monitorElementPositionInterval = setInterval(this.monitorElementPosition, MONITOR_INTERVAL);
  }

  private disableFloatingFocus() {
    this.container.classList.remove(CLASS_CONTAINER_ENABLED);
    this.enabled = false;
    clearInterval(this.monitorElementPositionInterval);
  }

  render() {
    const classes = {
      "stzh-flyingfocus": true,
      "stzh-flyingfocus--is-enabled": this.enabled,
      "stzh-flyingfocus--is-visible": this.visible,
      "stzh-flyingfocus--is-moving": this.moving,
      "stzh-flyingfocus--has-helper": this.helper
    };

    const style = {
      ...(this.style || {}),
      ...(this.position || {})
    };

    return (
      <Host>
        <div
          class={classes}
          style={style}
          onTransitionEnd={this.moving ? this.handleTransitionEnd : null}
        ></div>
      </Host>
    );
  }
}
